import { IFinancial } from '../../../../data/entities/customer/financial';

import * as React from 'react';

import { IValidationRule } from '../../../../view/pages/customer/validation';
import { Accordion, AccordionHeader, AccordionItem } from '../../../components/accordion';
import { Form, FormInput, FormLabel, FormLabelHelp, FormRow, FormSelect } from '../../../components/form';
import { Heading } from '../../../components/heading/Heading';
import { Loader } from '../../../components/loader/Loader';
import { Panel } from '../../../components/panel/Panel';
import { Paragraph } from '../../../components/paragraph/Paragraph';
import { withValidation } from '../../../components/validation/ValidationWrap';
import { IValidationResult } from '../validation';

interface IPaymentDetailsProps {
	financial: IFinancial;
	validation: IValidationResult[];
	onChangeFinancial: (key: keyof IFinancial, value: IFinancial[keyof IFinancial]) => any;
}

export enum SupportedPaymentTypes {
	UK_BANK = 'UK bank account',
	INTL_BANK = 'IBAN/SWIFT',
	NON_IBAN = 'Non-IBAN account'
}

export type PaymentTypeLookup = {
	[key in SupportedPaymentTypes]: {
		requiredData: Partial<IFinancial>,
		validationRules: { [k in keyof Partial<IFinancial>]: IValidationRule },
		display: { [k in keyof Partial<IFinancial>]: { displayName: string, help?: string, inputTransform?: (input: string) => string } },
	};
};

export type UKBank = Pick<IFinancial, 'account_holder_name' | 'account_number' | 'sort_code'>;
export type InternationalBank = Pick<IFinancial, 'account_holder_name' | 'iban' | 'swift'>;
export type PayPal = Pick<IFinancial, 'paypal_account'>;

export const PaymentTypeInfo: PaymentTypeLookup = {
	[SupportedPaymentTypes.UK_BANK]: {
		requiredData: { account_holder_name: '', sort_code: '', account_number: '' } as UKBank,
		validationRules: { sort_code: { level: 'mandatory', min: 6, max: 6, desc: 'UK Sort Codes should be 6 digits' } },
		display: {
			sort_code: {
				displayName: 'Sort Code', help: 'This is usually a 6 digit number',
				inputTransform: (input) => input.replace(/\s/g, '')
			},
			account_holder_name: { displayName: 'Account Name' },
			account_number: {
				displayName: 'Account Number', help: 'This is usually an 8 digit number',
				inputTransform: (input) => input.replace(/\s/g, '').toUpperCase()
			},
		}
	},
	[SupportedPaymentTypes.INTL_BANK]: {
		requiredData: { account_holder_name: '', iban: '', swift: '' },
		validationRules: {},
		display: {
			swift: {
				displayName: 'SWIFT', help: 'This is an 8-11 character code',
				inputTransform: (input) => input.replace(/\s/g, '').toUpperCase()
			},
			account_holder_name: { displayName: 'Account Name' },
			iban: {
				displayName: 'IBAN', help: 'This is an up to 43 character code',
				inputTransform: (input) => input.replace(/\s/g, '').toUpperCase()
			}
		}
	},
	[SupportedPaymentTypes.NON_IBAN]: {
		requiredData: { account_holder_name: '', intl_account_number: '', swift: '' },
		validationRules: {},
		display: {
			swift: {
				displayName: 'SWIFT', help: 'This is an 8-11 character code',
				inputTransform: (input) => input.replace(/\s/g, '').toUpperCase()
			},
			account_holder_name: { displayName: 'Account Name' },
			intl_account_number: {
				displayName: 'Account number', help: '',
				inputTransform: (input) => input.replace(/\s/g, '').toUpperCase()
			}
		}
	}
};

const FormInputWithValidation = withValidation(FormInput);
const FormSelectWithValidation = withValidation(FormSelect);

export class PaymentDetailsEdit extends React.Component<IPaymentDetailsProps> {
	public render() {
		const { financial } = this.props;
		const validation = this.props.validation;
		const matchedPaymentType = PaymentTypeInfo[financial.payment_method];

		return !financial ? <Loader loading={true} /> : (
			<div className="customer__payment-details">
				<Accordion>
					<AccordionHeader>Payment details</AccordionHeader>
					<AccordionItem>
						<Form>
							<Panel withIcon={true}>Changes to these details can take up to 7 days to take effect.</Panel>
							{!!validation && <>
								<Panel withIcon={true}>
									We need both your business details and payment details in order to pay you for bookings. Please fill in both sections and save your changes.
								</Panel>
							</>}
							<Heading type="B1">Payment Details</Heading>
							<FormRow>
								<Paragraph fillParent="full">
									Please select the method by which you would like to be paid and fill out each field.
								</Paragraph>
								<FormSelectWithValidation
									name="payment_method"
									value={financial.payment_method}
									onChange={(e: React.ChangeEvent<HTMLSelectElement>) => this.props.onChangeFinancial('payment_method', e.target.value)}
									validation={validation}
									hideValidationUntilBlurred
									required
								>
									<option key="payment_type_none" value="">Select a payment method...</option>
									{Object.keys(SupportedPaymentTypes).map((k) => {
										const val = SupportedPaymentTypes[k];
										return <option key={`payment_type_${k}`} value={val}>{val}</option>;
									})}
								</FormSelectWithValidation>
							</FormRow>
							<Heading type="B1">{financial.payment_method}</Heading>
							<Paragraph fillParent="full">
								Please ensure that the payment details are entered correctly so that payments processed to you will be successful.
								In particular, please make sure the Account Name is the name the bank account is registered with and therefore appears
								on your bank statements.
								<br />
								<br />
								<strong>Note that we cannot accept IBAN accounts based in Russia or Belarus.</strong>
							</Paragraph>
							{(() => {
								if (matchedPaymentType) {
									return Object.keys(matchedPaymentType.requiredData).map((rd) => {
										const displayInfo = matchedPaymentType.display[rd];
										return <FormRow key={`row_payment_${rd}`}>
											<FormLabel htmlFor={rd}>{displayInfo.displayName}</FormLabel>
											{displayInfo.help && <FormLabelHelp>{displayInfo.help}</FormLabelHelp>}
											<FormInputWithValidation
												key={`payment_${rd}`}
												name={rd}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													let input = e.target.value;
													if (displayInfo.inputTransform) {
														input = displayInfo.inputTransform(input);
													}
													this.props.onChangeFinancial(rd as keyof IFinancial, input);
												}}
												type="text"
												required
												hideValidationUntilBlurred
												id={`payment_${rd}`}
												value={financial[rd]}
												validation={validation}
											/>
										</FormRow>;
									});
								} else {
									return null;
								}
							})()}
						</Form>
					</AccordionItem>
				</Accordion>
			</div>
		);
	}
}
